import Backbone from 'backbone'
import DataSourceWidgetView from '../../data-source-widget-view'
import _ from 'lodash'
import ShapeConfig from '../config/shape-config'
import draw from './draw'
import initiativeCalculationTypes from 'scoreboard/initiatives/initiative-calculation-types'
import nonAbbreviatedValuesTooltip from 'scoreboard/components/non-abbreviated-values-tooltip.hbs'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'

import {
	SHAPE_COLOR_TYPE, SHAPE_SPECIFIC_COLOR, SHAPE_SPECIFIC_TEXT,
	SHAPE_TEXT_BOLD, SHAPE_TEXT_UNDERLINE, SHAPE_TEXT_ITALIC,
	HANDLES, TIP_SIZE, SHAPE_TEXT_ROTATION, SHAPE_OPACITY
} from '../shape-widget-properties'

import template from './shape-view.hbs'
import datasetWidgetDrilldown from '../../dataset-widget-drilldown'

const ShapeView = DataSourceWidgetView.extend({
	className: 'widget shape unpadded-resize-controls',

	resizable () {
		return {
			width: true,
			height: this.model.properties.get('shape') !== 'line'
		}
	},

	autoResize: true,
	configClass: ShapeConfig,
	template,

	lockedAR () {
		if (this._adjustingHeight) {
			// If we're in the middle of adjusting the height, we need to tell
			// canvas the aspectRatio isn't locked, so it just accepts our
			// new dimensions (which *will* respect the aspect ratio). This is
			// important when changing the shape to one with a lockedAR
			return false
		}

		return LOCKED_AR_SHAPES.includes(this.model.properties.get('shape'))
	},

	nonFetchProperties: DataSourceWidgetView.prototype.nonFetchProperties.concat(
		'shapeHeight', 'fill', 'shape', 'textColor', 'label',
		SHAPE_TEXT_BOLD, SHAPE_TEXT_UNDERLINE, SHAPE_TEXT_ITALIC,
		SHAPE_COLOR_TYPE, SHAPE_SPECIFIC_COLOR, SHAPE_SPECIFIC_TEXT,
		HANDLES, TIP_SIZE, SHAPE_TEXT_ROTATION, SHAPE_OPACITY,
		// These scorecard node options use data that is always sent anyway
		'showScore', 'showGoal', 'showActual', 'showPeriod',
		// These initiative options use data that is always sent anyway
		'showBudget', 'showBudgetRemaining', 'showBudgetSpent', 'showBudgetVariance', 'showBudgetVariancePercent',
		'showDaysElapsed', 'showEndDate', 'showPercentComplete', 'showPercentTimeElapsed', 'showProjectedBudget',
		'showProjectedEndDate', 'showScheduleVariance', 'showStartDate', 'textAlign', 'showAbbreviatedNumbers',
		// Used for both scorecards and initiatives and always sent anyway
		'showLabel', 'showColor'
	).concat(initiativeCalculationTypes.showPropertyNames()),

	initialize: function () {
		this.rendered = false
		DataSourceWidgetView.prototype.initialize.apply(this, arguments)
		_.bindAll(this, 'resize', 'lockedAR', 'resizable')

		this.on('transitioned:shape', () => {
			// after the transition to a different shape is done, we need to update
			// the connectors so they still attach to the edge of the shape
			let wobId = this.model.wob.id
			_(this.canvas.collection.toArray())
				// get all connectors on all wobs
				.flatMap(w => w.get('connectors').toArray())
				// filter down to any connectors that come from or go to the changed wob
				.filter(c => c.get('fromId') == wobId || c.get('toId') == wobId)
				// get their corresponding collection (which has the update method we need)
				.map(c => c.collection)
				.uniq()
				// update all the affected connectors
				.forEach(connectors => connectors.update())
		})

		this.listenTo(this.model.wob, 'change:rotate',() => {
			// When the wob is rotated, we only need to call render() on the
			// shape itself (instead of all the logic for colors, labels, etc)
			this.shape?.render(this.model.shapeDimensions(),
				this.currentShapeOptions)
		}, this)

		Array.from(['undo', 'redo']).forEach(event => {
			this.listenTo(this.canvas.history, event, () => {
				// When an undo/redo happens, we can get out of sync with part
				// of the changes happening before the rest, and we don't want
				// transitions, so it's cleanest to just remove the shape and
				// re-render it
				if (this.shape) {
					this.$('.svg-container svg').remove()
					delete this.shape
				}
			})
		})
	},

	// Defines the object that controls where the connectors should intersect
	connectorContainer: function () {
		return this.el.querySelector('svg path.symbol-outline')
	},

	// Returns the shape's bounding box, which could exceed the wob's boundaries
	boundingBox: function () {
		// For dataset widgets, there might not be a shape yet (because the
		// query hasn't completed yet). It's fine to return null for this
		// method, though, so that's what we'll do here
		return this.shape?.boundingBox()
	},

	adjustHeight: function () {
		if (this._resizing) {
			return
		}

		if (this.$el.hasClass('has-widget-empty-state')) {
			// Don't adjust the height if we're in error state; presumably the
			// error will eventually be resolved, and we want the same height
			return
		}

		this._adjustingHeight = true

		// We don't want to listen to resized events while we're rendering,
		// because the height may increase and we'll be manually adjusting
		// the height, we don't want to get in an infinite loop
		this.container
			// just to make sure we don't double-bind the listeners
			.off('resized', this.resize)

		// Now that all rendering is done, this will let the wob know that
		// the dimensions may need to change. Add height for each visible portion
		// of the widget.
		let newHeight = 0

		// The bubble itself
		newHeight += this.$('.shape-wrapper').get(0).clientHeight
		// The rest (legend, description, padding)
		newHeight += this.nonBubbleHeight()

		this.model.wob.resize(null, newHeight, undefined, undefined,
			// Need to user force: true for line widgets, since they normally
			// do not allow the height to be changed
			{ force: true })

		// And now we can listen for standard resized events again
		this.container
			.on('resized', this.resize)

		this._adjustingHeight = false
	},

	nonBubbleHeight: function () {
		let height = 0

		// Description showing? If so, add it to the height
		let description = this.$('.description')
		if (description.length > 0) {
			// #30454 using outerHeight(true) so it accounts for margin
			height += description.outerHeight(true)
		}

		// Legend showing? If so, add it to the height
		let legend = this.$('.legend')
		if (legend.length > 0) {
			// #30454 using outerHeight(true) so it accounts for margin
			height += legend.outerHeight(true)
		}

		// Widget showing a background? If so, add more height to
		// compensate for the extra background border (top + bottom).
		if (this.model.properties.get('background') !== 'none')  {
			height += 2 // 2px top & bottom border
		}

		// Label showing? If so, add it to the height
		if (!_.isNil(this.model.properties.get('label'))) {
			height += this.$('.title-text').outerHeight()
		}

		return height
	},

	render: function () {
		if (this.adjustingHandles) {
			// We're updating the widget's properties to reflect the changes
			// that were already made in impact-shape for the handles; do not
			// re-render
			return this
		}

		DataSourceWidgetView.prototype.render.apply(this, arguments)

		return this
	},

	renderPlaceholderContent () {
		// Just draw the SVG with no data and placeholder class so it can be
		// styled appropriately
		this.togglePlaceholderState(true)
		this._draw()
	},

	renderDatasetError (errorResponse) {
		this.togglePlaceholderState(false)
		DataSourceWidgetView.prototype.renderDatasetError.apply(this, arguments)
	},

	/**
	 * Sets whether the widget is in a placeholder state (ie initial render done,
	 * but waiting for the dataset query).
	 *
	 * Note that this is not the same as the "loading" state, which also occurs
	 * when the calendar period changes, dataset filters change, etc.
	 *
	 * @param {boolean} isPlaceholder
	 */
	togglePlaceholderState (isPlaceholder) {
		this.isPlaceholder = isPlaceholder
	},

	/**
	 * @override
	 */
	renderContent ({ datasetQueryResponse } = {}) {
		this.togglePlaceholderState(false)

		// Always redraw the SVG
		this._draw()

		// Nix the non-abbreviated tooltip if it exists. We might be re-rendering
		// because of a period change in which case we'll need to start fresh.
		if (this.nonAbbreviatedTooltip) {
			this.nonAbbreviatedTooltip.remove()
			this.nonAbbreviatedTooltip = null
		}

		// Apply a new non-abbreviated tooltips as needed
		this.nonAbbreviatedTooltip = new TooltipAndDetails({
			el: this.$el,
			selector: '[data-unshortened-value]',
			tooltipContent: (e) => nonAbbreviatedValuesTooltip({
				header: e.target.dataset.unshortenedHeader,
				value: e.target.dataset.unshortenedValue
			}),
			tooltipOptions: {
				extraClass: 'tooltip-and-details non-abbreviated-tooltip performance-bubble-abbreviated-tooltip ',
				placement: 'above'
			}
		})

		this.canvas.onPaint(() => {
			// #30097 if there are multiple bubbles, canvas's onPaint() queue ends up
			// being something like this on initial render:
			//
			//  1. wob1 paintable.js paint() - sets wob1 dimensions
			//  2. wob1 this.adjustHeight() - triggers reflow on call to clientHeight
			//  3. wob2 paintable.js paint() - sets wob2 dimensions (invalidating layout)
			//  4. wob2 this.adjustHeight() - triggers reflow AGAIN on call to clientHeight
			//  5. (repeated reflow thrashing for every wob)
			//
			// By making the adjustHeight() calls in nextTick(), we make sure that
			// all of the initial wob dimension setting is done before we start our
			// calls to clientHeight, limiting us to a single reflow and dramatically
			// improving performance on dashboards with a large number of bubbles.
			//
			// Note that which the performance hit was far worse on Safari, there
			// was a measurable difference on Chrome and Firefox, too
			process.nextTick(() => {
				// #24145 the non-svg content (like the description) may have changed
				// since the bubble was originally created, thus changing the height
				// of the widget. So we need to adjust it.
				//
				// But we won't know what the height should be until the canvas
				// has finished painting.
				this.adjustHeight()
				this.readyForDisplayAndExport()
			})
		})

		return this
	},

	_draw: function () {
		// #18748 switched to using wob model's dimensions instead of
		// clientWidth/clientHeight to improve performance on large dashboards
		let container = this.model.wobDimensions()
		let shapeDimensions = this.model.shapeDimensions()

		var domainData = this.model.relevantData({
			datasetQueryResponse: this.lastDatasetQueryResponse
		})

		var data = {
			properties: domainData.properties,
			domainData: domainData,
			shapeWidth: shapeDimensions.width,
			shapeHeight: shapeDimensions.height,
			width: container.width,
			height: container.height,
			isPlaceholder: this.isPlaceholder,
			cid: this.model.cid
		}

		draw(this, data)

		this.toggleEmptyState(domainData.error)
	},

	resize: function (e, dimensions) {
		var props = this.model.properties.toJSON()
		let oldBubbleHeight = parseFloat(props.shapeHeight)

		if (this.autoAdjusting) {
			// #21877 When drag-resizing is finished (this.autoAdjusting === true),
			// we automatically adjust the wob dimensions to make sure everything
			// still fits. But we also need to make sure the shapeHeight
			// doesn't go below the minimum height
			let minHeight = this.model.getMinShapeDimension()
			if (oldBubbleHeight < minHeight) {
				this.model.properties.set({ shapeHeight: minHeight })
			}

		} else if (!this.autoAdjusting && dimensions && dimensions.previous) {
			// This section is called once for each resize event fired while dragging.
			//
			// Since the aspect ratio isn't locked, and the height could vary depending on what options are selected
			// and how long the description is, we need to adjust the bubble image's height only by the amount
			// that the wob's height was increased

			let shapeHeight
			if (dimensions.height != dimensions.previous.height && dimensions.width == dimensions.previous.width) {
				// #24093 If only the height has changed, it might be due to using the
				// widget config menu Size option. In that case, we need to account for
				// the possibility that the bubble might have 0 height, but also has a legend.
				//
				// In that case, keep shapeHeight at 0 until the wob is large enough to
				// fit both the bubble and its description/lkegend
				let nonBubbleHeight = this.nonBubbleHeight()

				if (this.model.properties.get('background') !== 'none') {
					// If there's a background, there's an extra 10px padding
					// around each side
					nonBubbleHeight += 20
				}

				shapeHeight = Math.max(0, dimensions.height - nonBubbleHeight)
			}

			if (_.isNil(shapeHeight)) {
				// Only increase the bubble height by the amount the size increased
				shapeHeight = Math.max(0, oldBubbleHeight + dimensions.height - dimensions.previous.height)
			}

			// We don't care about the width, because it will always be the full width of the wob
			this.model.properties.set({ shapeHeight })

			// When we set the shapeHeight, it will fire a change:shapeHeight event on the model only if the height changed.
			// When that happens, render will be called, which will then call _draw. But for other resize types, such as adjusting
			// the width we need to make sure the SVG is redrawn
			this._draw()
		}
	},

	getAdjustedSize: function () {
		let dim = DataSourceWidgetView.prototype.getAdjustedSize.apply(this, arguments)
		// If there's a background to this widget, account for the border around our .content
		// which isn't factored in with clientHeight/Width.
		if (this.model.properties.get('background') !== 'none')  {
			dim.height += 2
			dim.width += 2
		}
		return dim
	},

	allowConfig: function () {
		// even if this is a template copy, we can still change the drilldown
		return true
	},

	/**
	 * See ShapeModel.datasetId()
	 * @return {Int}
	 */
	datasetId () {
		return this.model.datasetId()
	},

	/**
	 * @override
	 */
	drilldown: datasetWidgetDrilldown,

	/**
	 * Handles the `change:data` event from impact-shape
	 *
	 * @param {Object} changes - the changes to data like handles, tipSize
	 * from impact-shape
	 *
	 * @param {Boolean} [firstRender=false] - whether this is the first render
	 * of the view
	 */
	onShapeChangeData ({ changes, firstRender }) {
		let changingShape = this.model.properties.hasChanged('shape')
		if (firstRender || changingShape || this._resizing) {
			// On the initial render of the shape, the handles/tipSize may be
			// null, and impact-shape will emit a `change:data` event. In that
			// case, we need to update the widget's properties with the actual
			// handles/tipSize, but we don't want a new undo/redo history record
			//
			// And when changing shape, the handles/tipSize are reset to null
			// (as if it's a new shape), so things should be handled the same.
			//
			// #57710 And when resizing some shapes, the handles will be adjusted
			// during the resize. Like with the initial render, we need to update
			// the widget's properties, but we don't want to create extra
			// undo/redo history records.
			//
			// The nextTick is important when changing shape, or we get into
			// trouble with a change event firing within another change event,
			// causing a double-render and breaking the transitions.
			process.nextTick(() => {
				this.canvas.history?.quietly(
					() => this.updateModelFromShapeChange(changes))
			})
		}

		// The only other `change:data` scenario we want to deal with is
		// internal handle dragging. However, we want to wait to update the
		// widget and create undo records until the handle dragging is complete
		// (ie 'handle:dragend' event), which is handled in onHandleDragEnd()
	},

	onHandleDragEnd (changes) {
		// Now that we're done dragging the internal shape handles, we can
		// cut an undo record and update the model
		this.model.trigger('transaction',
			() => this.updateModelFromShapeChange(changes))
	},

	/**
	 * Applies the given changes from impact-shape's change:data event
	 */
	updateModelFromShapeChange (changes) {
		// Set a flag so that the view won't re-render; impact-shape has already
		// rendered the changes, and is just notifying Impact so that we can update
		// the widget properties
		this.adjustingHandles = true

		// #57819 undo actions can get us in a weird state where things are
		// half-updated and we're firing change events. So make sure any
		// property setting is done silently
		if (changes.handles) {
			this.model.setPropertySilently('handles', JSON.stringify(changes.handles))
			_.extend(this.currentShapeOptions, _.pick(changes, 'handles'))
		}
		if (!_.isNil(changes.tipSize)) {
			this.model.setPropertySilently('tipSize', changes.tipSize)
			_.extend(this.currentShapeOptions, _.pick(changes, 'tipSize'))
		}

		this.adjustingHandles = false
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.nonAbbreviatedTooltip], 'remove')
	}
})

/**
 * The padding added around the shape svg's height when a background
 * is turned on. This corresponds to the top/bottom padding in
 * shape-widget.scss for .shape-wrapper
 */
const BACKGROUND_PADDING_HEIGHT = 20

/**
 * The padding added around the shape svg's width when a background
 * is turned on. This corresponds to the left/right padding in
 * shape-widget.scss for .shape-wrapper
 */
const BACKGROUND_PADDING_WIDTH = 30

// List comes from #56521
const LOCKED_AR_SHAPES = [ 'cloud', 'seal', 'star' ]

export default ShapeView
export { BACKGROUND_PADDING_HEIGHT, BACKGROUND_PADDING_WIDTH, LOCKED_AR_SHAPES }

ShapeView.__filename__ = 'dashboards/widgets/shape/view/shape-view.js'
